import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { CreateWorkOrderDocument, CreateWorkOrderLocationsDocument, GetWorkOrderDocument, SaveWorkOrderDocument } from 'types/db';
import { type ArgoWorkOrderInsertInput } from '@redviking/argonaut-util/types/db-schema/argo/work_order';
import { ArgoWorkOrderRestrictionEnum } from '@redviking/argonaut-util/types/db-enums/argo/work_order_restriction';
import { ArgoWorkOrderStatusEnum } from '@redviking/argonaut-util/types/db-enums/argo/work_order_status';
import { Route } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import { Notify } from 'src/notifications';
import _cloneDeep from 'lodash.clonedeep';
import { ValidationResult } from 'types';
import {
    type EntityModifyParameters,
    type ExtendedEntityParameters,
} from 'src/components/EntityDetail';
import { validateWorkOrder, validateWorkOrderForCreation } from './workOrder.validations';
import { getListChanges } from 'src/util/composables/watch-list-changes';

export type WorkOrderCreateEntity = {
    // NOTE: we are not including id for the work order here, it will be created by the database
    // NOTE: we are not including status, it will default to 'staged' on creation
    // NOTE: we are not including actualStart, it will be null on creation
    // NOTE: we are not including ids for the location links, they will be created by the database
    locations: string[];
    orderNumber: string;
    targetQuantity: number;
    materialModelId: string;
    plannedStart: Date | 'now';
    restriction: ArgoWorkOrderRestrictionEnum;
};

export type WorkOrderEditEntity = {
    id: string;
    createdAt: string;
    plannedStart: Date;
    orderNumber: string;
    targetQuantity: number;
    materialModelId: string;
    updatedAt: string | null;
    materialOtherCount: number;
    materialCompletedCount: number;
    status: ArgoWorkOrderStatusEnum;
    actualStart: Date | null | 'now';
    restriction: ArgoWorkOrderRestrictionEnum;
    locations: { id: string, location_id: string }[];
    materials: { id: string, material_id: string }[];
};

export type WorkOrderEntity = WorkOrderCreateEntity | WorkOrderEditEntity;

export async function getEntity (to: Route): Promise<{ entity: WorkOrderEntity, originalEntity?: WorkOrderEntity }> {

    if (to.query.mode === 'create') {
        const workOrder: WorkOrderEntity = {
            locations: [],
            orderNumber: '',
            targetQuantity: 0,
            materialModelId: '',
            plannedStart: 'now',
            restriction: ArgoWorkOrderRestrictionEnum.None,
        };
        return { entity: workOrder };
    } else {
        const { workOrder } = await gqlClient.request({
            document: GetWorkOrderDocument,
            variables: {
                work_order_id: to.params.workOrderId,
            },
        });

        if (!workOrder) {
            const err = new Error('Unknown Work Order');
            Notify.error(err);
            throw err;
        }

        // convert the graphql structure to our entity type
        let originalEntity: WorkOrderEditEntity = {
            createdAt: workOrder.created_at,
            id: workOrder.id,
            orderNumber: workOrder.order_number,
            materialModelId: workOrder.material_model_id,
            status: workOrder.status,
            restriction: workOrder.restriction,
            targetQuantity: workOrder.target_quantity,
            plannedStart: new Date(workOrder.planned_start),
            actualStart: workOrder.actual_start ? new Date(workOrder.actual_start) : null,
            locations: workOrder.work_order_locations.map(locLnk => ({
                id: locLnk.id,
                location_id: locLnk.location_id,
            })),
            materials: workOrder.work_order_materials.map(matLnk => ({
                id: matLnk.id,
                material_id: matLnk.material_id,
            })),
            materialCompletedCount: workOrder.material_completed_count || 0,
            materialOtherCount: workOrder.material_other_count || 0,
            updatedAt: workOrder.updated_at || null,
        };

        if (to.query.mode === 'copy') {
            originalEntity = _cloneDeep(originalEntity);
            originalEntity.id = uuidv4();
            originalEntity.orderNumber = `${originalEntity.orderNumber} - Copy`;
            originalEntity.status = ArgoWorkOrderStatusEnum.Staged;
            originalEntity.actualStart = null;
            originalEntity.materials = [];
        }

        const entity = _cloneDeep(originalEntity);

        return {
            entity,
            originalEntity,
        };
    }
}

export async function saveEntity (payload: ExtendedEntityParameters<'workOrder'>): Promise<ValidationResult> {
    const entity = payload.entity as WorkOrderEditEntity;
    const result = validateWorkOrder(entity);
    if (result.status !== 'success') {
        return result;
    }

    if (!payload.oldEntity) {
        return {
            status: 'error',
            errorMessage: 'No original entity to compare against',
        };
    }
    const oldEntity = payload.oldEntity as WorkOrderEditEntity;

    const workOrderLocationPlc = getListChanges(entity.locations, oldEntity.locations, { deep: true, format: 'plc' });
    const workOrderMaterialPlc = getListChanges(entity.materials, oldEntity.materials, { deep: true, format: 'plc' });

    const workOrderInput: ArgoWorkOrderInsertInput = {
        status: entity.status,
    };
    if (entity.actualStart && entity.actualStart !== 'now') {
        workOrderInput.actual_start = entity.actualStart.toISOString();
    }

    const {
        update_argo_work_order_by_pk,
    } = await gqlClient.request({
        document: SaveWorkOrderDocument,
        variables: {
            workOrderInput,
            workOrderId: entity.id,
            insertWorkOrderLocations: workOrderLocationPlc.inserts.map(plc => ({
                work_order_id: entity.id,
                location_id: plc.location_id,
            })),
            deleteWorkOrderLocationBoolExp: {
                id: {
                    _in: workOrderLocationPlc.deletes.map(plc => plc.id),
                },
            },
            insertWorkOrderMaterials: workOrderMaterialPlc.inserts.map(plc => ({
                work_order_id: entity.id,
                material_id: plc.material_id,
            })),
            deleteWorkOrderMaterialBoolExp: {
                id: {
                    _in: workOrderMaterialPlc.deletes.map(plc => plc.id),
                },
            },
        },
    });

    if (!update_argo_work_order_by_pk) {
        return {
            status: 'error',
            errorMessage: 'Failed to save Work Order',
        };
    }

    return {
        status: 'success',
    };
}

export const createWorkOrder = async (payload: EntityModifyParameters<'workOrder'>): Promise<ValidationResult> => {
    const entity = payload.entity as WorkOrderCreateEntity;

    const result = validateWorkOrderForCreation(entity);
    if (result.status !== 'success') {
        return result;
    }

    const workOrderInput: ArgoWorkOrderInsertInput = {
        restriction: entity.restriction,
        order_number: entity.orderNumber,
        target_quantity: entity.targetQuantity,
        material_model_id: entity.materialModelId,
    };
    if (entity.plannedStart !== 'now') {
        workOrderInput.planned_start = entity.plannedStart.toISOString();
    }

    const {
        insert_argo_work_order_one: newWorkOrder,
    } = await gqlClient.request({
        document: CreateWorkOrderDocument,
        variables: {
            workOrder: workOrderInput,
        },
    });

    if (!newWorkOrder) {
        return {
            status: 'error',
            errorMessage: 'Failed to create Work Order',
        };
    }

    const {
        insert_argo_work_order_location: newWorkOrderLocations,
    } = await gqlClient.request({
        document: CreateWorkOrderLocationsDocument,
        variables: {
            insertWorkOrderLocations: entity.locations.map<{
                location_id: string;
                work_order_id: string;
            }>(locationId => ({
                location_id: locationId,
                work_order_id: newWorkOrder.id,
            })),
        },
    });

    if (!newWorkOrderLocations) {
        return {
            status: 'error',
            errorMessage: 'Failed to create Work Order Locations',
        };
    }

    return {
        status: 'success',
        newRoute: {
            name: 'workOrderDetail',
            params: {
                workOrderId: newWorkOrder.id,
            },
        },
    };
};
